import {
 useState, useCallback
} from 'react';
import {
 Settings, Trash2, Move
} from 'lucide-react';
import {
 Button
} from 'shared/components/ui/button';
import {
 Card, CardContent,CardHeader, CardTitle
} from 'shared/components/ui/card';
import {
 OpCode
} from '../types/storyBuilder.types';
import {
 getOpCodeIcon, renderSectionPreview
} from './utils';

interface Section {
  id: string;
  opCodeId: string;
  config: Record<string, any>;
}

interface SectionListProps {
  sections: Section[];
  getOpCodeById: (id: string) => OpCode | undefined;
  onOrderChange: (newOrder: Section[]) => void;
  onRemove: (sectionId: string) => void;
  onConfigure: (section: Section) => void;
}

export default function SectionList({
  sections,
  getOpCodeById,
  onOrderChange,
  onRemove,
  onConfigure
}: SectionListProps): React.JSX.Element {
  const [draggedItem, setDraggedItem] = useState<Section | null>(null);

  const handleDragStart = useCallback(
    (e: React.DragEvent, index: number) => {
      const target = e.target as HTMLElement; // Cast to HTMLElement to access style
      setDraggedItem(sections[index]);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', index.toString());
      target.style.opacity = '0.5'; // Accessing style on HTMLElement
    },
    [sections]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, hoverIndex: number) => {
      e.preventDefault();
      const dragIndex = Number(e.dataTransfer.getData('text/plain'));
      const newSections = [...sections];
      newSections.splice(dragIndex, 1);
      newSections.splice(hoverIndex, 0, draggedItem!); // Non-null assertion since draggedItem is set when dragging starts
      onOrderChange(newSections);
      setDraggedItem(null);
    },
    [sections, draggedItem, onOrderChange]
  );

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    const target = e.target as HTMLElement;
    target.style.opacity = '1'; // Reset opacity after drag ends
    setDraggedItem(null);
  }, []);

  return (
      <div className="space-y-6">
          {sections.map((section, index) => {
        const opCode = getOpCodeById(section.opCodeId);
        if (!opCode) return null;

        return (
            <Card
            key={section.id}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
          >
                <CardHeader className="flex flex-row items-center space-y-0">
                    <div className="flex-1 flex items-center gap-4">
                        <Move className="h-5 w-5 text-muted-foreground cursor-move" />
                        <CardTitle className="flex items-center gap-2">
                            {getOpCodeIcon(opCode.type)}
                            {opCode.name}
                        </CardTitle>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={() => onConfigure(section)}>
                            <Settings className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => onRemove(section.id)}>
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>{renderSectionPreview(opCode)}</CardContent>
            </Card>
        );
      })}
      </div>
  );
}
